import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, take } from 'rxjs';
import { CONSTANTS } from '../../Constants/Constants';
import {
  CollaborationState,
  CostReviewStatus,
  UserCostReviewThresholdRequest,
  UserCostReviewThresholdResponse
} from '../../models/dtos';
import { CommonDataService } from '../common-data.service';
import { CommonMetaDataService } from '../common-meta-data.service';
import { UserProfileService } from '../common/user-profile.service';
import { SolEstimateService } from '../estimate/sol-estimate.service';
import { GetReviewDetailsService } from '../api/get-review-details.service';
import { CostReviewEstimateType, IOptionalWorkflowAndEstimateType, ReviewDetailTabsService } from 'review-detail-tabs';
import { CommonFunctionsService } from '../common-functions.service';

@Injectable({ providedIn: 'root' })
export class CostReviewFunctionsService {
  private userCostReviewThresholdValue: number | null = null;
  private isCostReviewUnderThreshold: boolean | null = null;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly solEstimateService: SolEstimateService,
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly reviewDetailTabsService: ReviewDetailTabsService,
    private readonly reviewDetailsAPIService: GetReviewDetailsService,
    private readonly userProfileService: UserProfileService,
  ) {
    this.subscribeToSellAsSellPriceChanges();
  }

  private subscribeToSellAsSellPriceChanges(): void {
    this.solEstimateService.onSellAsSellPriceUpdated$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged()
      )
      .subscribe({
        next: (sellAsSellPrice: number) => {
          const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
          if (estimateTab) {
            estimateTab.soldASSellPrice = sellAsSellPrice;
          }

          this.evaluateReviewTabState(sellAsSellPrice);
        },
        error: (error) => {
          console.error('Error in onSellAsSellPriceUpdated$ subscription:', error);
        }
      });
  }

  /**
   * Re-evaluates the estimate type and review tab visibility from the cached threshold.
   * Call after the cost review status changes (request/cancel/unlock/refresh) so a routed
   * review keeps the tab visible and a cancelled one falls back to the sell price threshold.
   */
  refreshReviewTabVisibility(): void {
    const sellAsSellPrice = this.commonDataService.getEstimateMetaData()?.estimateTab?.soldASSellPrice || 0;
    this.evaluateReviewTabState(sellAsSellPrice);
  }

  private evaluateReviewTabState(sellAsSellPrice: number): void {
    if (this.userCostReviewThresholdValue === null) {
      return;
    }

    this.setEstimateTypeBasedOnSellPrice(this.userCostReviewThresholdValue, this.isCostReviewUnderThreshold);
    this.handleReviewTabVisibility(this.userCostReviewThresholdValue, sellAsSellPrice, this.isCostReviewUnderThreshold);
  }

  isCostReviewAlreadyRouted(): boolean {
    const reviewStatus = this.commonDataService.getCostReviewData()?.status || "";
    return reviewStatus === CostReviewStatus.InProcess || reviewStatus === CostReviewStatus.Approved || reviewStatus === CostReviewStatus.Pending;
  }

  setEstimateTypeBasedOnSellPrice(userCostReviewThresholdValue: number, isCostReviewUnderThreshold: boolean) {
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;

    if (estimateTab && estimateTab.soldASSellPrice) {
      let data: IOptionalWorkflowAndEstimateType;
      const sellPrice = estimateTab.soldASSellPrice;
      if (sellPrice <= userCostReviewThresholdValue && isCostReviewUnderThreshold) {
        data = {
          optionalWorkflow: true,
          optionalEstimateType: CostReviewEstimateType.EstimateLess50K
        }
      } else if(sellPrice <= userCostReviewThresholdValue && this.isCostReviewAlreadyRouted()) {
        data = {
          optionalWorkflow: false,
          optionalEstimateType: CostReviewEstimateType.EstimateLess50K
        };
      } else if (sellPrice <= CONSTANTS.MAX_ESTIMATE_VALUE) {
        data = {
          optionalWorkflow: false,
          optionalEstimateType: CostReviewEstimateType.Estimate50_150K
        }
      }
      else {
        data = {
          optionalWorkflow: false,
          optionalEstimateType: CostReviewEstimateType.EstimateGreater150K
        }
      }
      this.reviewDetailTabsService.setOptionalWorkflowAndEstimateType(data);
    }
  }

  getUserCostReviewThreshold() {
    const requestBody = this.buildUserCostReviewThresholdRequest(
      this.userProfileService.userInfo?.mail || CONSTANTS.EMPTY_STRING
    );
    this.reviewDetailsAPIService.getUserCostReviewThreshold(requestBody)
      .pipe(take(CONSTANTS.ONE))
      .subscribe({
        next: (response: UserCostReviewThresholdResponse) => {
          if (response) {
            this.userCostReviewThresholdValue = response.threshold;
            this.isCostReviewUnderThreshold = response.isCostReviewUnderThreshold;
            this.refreshReviewTabVisibility();
          }
        },
        error: () => {
          console.error('API error:');
        }
      });
  }

  buildUserCostReviewThresholdRequest(emailId: string): UserCostReviewThresholdRequest {
    const estimateData = this.commonDataService.getEstimateData();
    const subSegment = this.commonDataService.getEstimateMetaData()?.estimateSettings?.subSegmentId ?? CONSTANTS.ZERO;
    // subsegmentid will always be 0 for 44OP estimates as per the desktop. for other estimate estimateSettings?.subSegmentId should be binded;
    const subSegmentId = this.commonFunctionsService.isQuotedServiceEstimate() ? subSegment : CONSTANTS.ZERO;
    return {
      emailId,
      segment: estimateData?.profitCenterId ?? CONSTANTS.ZERO,
      businessHierarchy: subSegmentId
    };
  }

  //Reset library save state
  resetLibrarySaveState() {
    this.reviewDetailTabsService.resetUnsavedChanges();
  }

  private handleReviewTabVisibility(userCostReviewThresholdValue: number, sellAsSellPrice: number, isCostReviewUnderThreshold: boolean): void {
    if (this.isEPSEstimate()) {
      this.commonDataService.setReviewTabVisibility(false);
      return;
    }

    if (userCostReviewThresholdValue === -1) {
      this.commonDataService.setReviewTabVisibility(false);
      return;
    }

    // A review raised earlier (e.g. while the optional workflow was enabled) must stay reachable
    // even if the optional workflow is now disabled and the sell price is under the threshold.
    if (this.isCostReviewAlreadyRouted()) {
      this.commonDataService.setReviewTabVisibility(true);
      return;
    }

    if(isCostReviewUnderThreshold && sellAsSellPrice > 0) {
      this.commonDataService.setReviewTabVisibility(true);
      return;
    }

    const optionalWorkflow = this.reviewDetailTabsService
      .getOptionalWorkflowAndEstimateType()?.optionalWorkflow;

    if (sellAsSellPrice > userCostReviewThresholdValue || optionalWorkflow) {
      this.commonDataService.setReviewTabVisibility(true);
    } else {
      this.commonDataService.setReviewTabVisibility(false);
    }
  }

  private isEPSEstimate(): boolean {
    const businessUnitId = this.commonDataService.getEstimateData()?.businessUnitID;
    const businessUnit = this.commonMetaDataService.getMetaData()?.businessUnits?.find(bu => bu.businessUnitId === businessUnitId)?.businessUnit;
    return businessUnit?.toUpperCase() === 'EPS';
  }

  private getReviewThresholdUserId(): string {
    const openEstimateResponse = this.commonDataService.getEstimateOpenResponse();
    const collaborationData = openEstimateResponse?.collaborationData;
    const isCollaborativeEstimate = collaborationData?.collaborationState
      && collaborationData.collaborationState !== CollaborationState.NonCollaborative;

    if (!isCollaborativeEstimate) {
      return this.userProfileService.userInfo?.gid || CONSTANTS.EMPTY_STRING;
    }

    const primaryUser = collaborationData?.collaboratorDataList?.find(collaborator => collaborator.isPrimaryUser);
    return primaryUser?.email || this.userProfileService.userInfo?.gid || CONSTANTS.EMPTY_STRING;
  }
}
